/**
 * payment-channel-readiness-adapter.ts —— 支付通道下发的**负向清单**（阻断项）纯函数。
 *
 * ## 为什么只做负向
 *
 * C 端把某通道列入候选的合取式在 `novel-app-server`
 * `src/services/payment-channel-candidates.ts` `buildBasePaymentChannelCandidates`：
 * `placement 已配置 ∩ 策略组成员 ∩ 至少一个可售产品 ∩（STRIPE 另需 active Checkout 配置）`。
 * 该合取式之后还有若干**用户级 / 场景级**收窄（SRV-PAY-003 / SRV-PAY-033 / new_user_offer 7 天窗），
 * 以及 STRIPE 专有的回跳 host 闸（C 端按自己那份 `PAYMENT_RETURN_HOSTS` 对该 App 的登记复核，在 C 端 Worker 进程内）。
 *
 * 故本模块**只回答「什么在阻止下发」，从不回答「会被下发」**：每条断言都是 admin 侧数据能独立
 * 证伪的必要条件，任一条命中即该通道确定不下发；全不命中只得到「本页所核 N 项未发现阻断项」，
 * 不构成「会下发」—— 残余判据逐条列在 `READINESS_BLIND_SPOTS`（**按通道分列，不是笼统「一项」**）。
 *
 * ## 四态，不是三态
 *
 * 早期版本只有「阻断 / 无阻断 / 无法判定」三态，**所有断言都预设「该通道该场景应当下发」**。
 * 现实里最多的一类是第四种：**不适用**。
 *
 * - 只卖 IAP 的 iOS App（全局策略组 = `[IAP]`）→ STRIPE 恒亮「3 项阻断」，而策略组面板把成员
 *   集合定义为通道启停的**唯一入口**：运营会同时看到「我关的」和「系统说被阻断」两种叙述。
 * - `app_payment_methods` 行只在运营配页时插入（`payment-pages-admin-service.ts` / copy App），
 *   **建 App 不按 scenario 全集预置** → 「某
 *   scenario 没有渠道行」是绝大多数 App 的常态，不是缺陷。
 *
 * 故 `notices`（中性项，**不计入 `blockerCount`**）与 `blockers` 分栏：**运营在策略组 / 支付页里
 * 的刻意配置结果一律进 `notices`**，只有「已建行但字段缺失 / 配置本身不合规」才算 `blockers`。
 * 这与 `app-payment-methods-adapter.ts` 移除 `app_disabled` 档是同一条取舍（不与策略组给出相反
 * 结论），只是那次是整档删除、这次是降档为中性。
 *
 * ## 维度
 *
 * - ⓪ App 启停 → per **App**，**全通道前置**（C 端 `findAppById` where 含 `is_active=true`）
 * - ① 策略组 → per `(app_id, region_code)`，故按 **region** 展开
 * - ② placement → `app_payment_methods` 的 where 含 `scenario`，故按 **scenario** 展开
 * - ③ Checkout 配置 → per **app**，**STRIPE 专有**
 * - ④ 可售产品 → per `(app, payment_method)`，**不含 scenario 维度**
 *   （C 端 `payment-products-service.ts` 用 `findProductMetadataCandidatesByApp` 全 App 取再 filter）
 *
 * ## fail-closed
 *
 * 任一数据源未就绪（加载中 / 未发起 / 失败 / 无权限）→ 对应项判 `undeterminable`，**绝不因
 * 「没查到阻断项」而渲染成「无阻断」**。判据是 `readinessSourceState`：**只有 `isSuccess` 才算
 * `ready`** —— TanStack Query 断网时 `fetchStatus='paused'` + `status='pending'`，此时
 * `isLoading`（`isPending && isFetching`）与 `isError` **同时为 false**，靠 `!isLoading && !isError`
 * 会把「什么都没拿到」判成 ready，再经 `?? []` 兜底成空集 → 报一屏凭空阻断。
 */

import type {
  AppPaymentMethodResponse,
  PaymentMethodOptionsResponse,
  PaymentMethodPolicyResponse,
  PaymentProviderConfigResponse,
  PaymentProviderTestResultReason,
  PaymentScenario,
  SettlementChannel,
  StripeCheckoutConfigResponse,
} from '@novel-admin/shared';
import {
  PAYMENT_SCENARIOS,
  STRIPE_REDIRECT_PATHS,
  isFrameworkSettlementChannel,
} from '@novel-admin/shared';
import { createPaymentMethodOptionLookup } from './payment-method-option-lookup';
import { SCENARIO_LABELS } from './payment-offer-adapter';
import type { ProductMetadataRow } from './product-metadata-adapter';

/**
 * 本面板按**结算通道**分栏（`03-数据字典/70-产品配置.md` §3 两层闭集）：④ 可售产品、③ Checkout 配置、
 * ② placement 三项都是 per 结算通道的事实，`product_metadata` / `app_payment_methods` /
 * `app_stripe_checkout_configs` 也都按通道建模。只有 ① 策略成员是**支付方式**层，故那一项必须先经
 * `channel_of` 投影再汇总（同一通道下的多个支付方式同进同出，§3.7.2 读取算法 ③）。
 *
 * 投影取 A8 §7.8.8 可选列表的 `channel`（目录行属性，admin 不另维护映射）：不在列表里的成员投影为 null、
 * 不计入任何通道。已退役成员与 C 端 discovery 一致（§3.7.2 ③ 只跳 RETIRED）；平台不适用的成员现仅有 WEB App
 * 的存量 IAP，C 端 WEB 发现路径同样不下发。原生 discovery 不按平台过滤，目录若收窄某行 platforms
 * （须先改 70 §3 映射表）本投影会与之分歧，届时随契约一并改。
 */
export const READINESS_CHANNEL_LABEL: Record<SettlementChannel, string> = {
  IAP: '应用内支付 (IAP)',
  STRIPE: '第三方支付 (Stripe)',
  PAYERMAX: 'PayerMax',
  AIRWALLEX: 'Airwallex',
};

/**
 * 本页核不到的残余判据 —— **如实告知信息边界，不是免责声明**，故按通道分别列全、不写「一项」。
 *
 * 通道差异的依据（C 端 `payment-channel-candidates.ts`）：
 * - provider readiness 闸只对结算通道 STRIPE 生效（srv 现行写法是先归一：
 *   `const settlementChannel = channelOf(member.paymentMethod)` → `if (settlementChannel === 'STRIPE' && !policy.hasActiveStripeCheckoutConfig) continue`）；
 * - `applyUserPaymentChannelGate`（SRV-PAY-003）与审核标签收窄（SRV-PAY-033）都是**收窄到 `{IAP}`**，
 *   即只可能剔除 STRIPE，从不剔除 IAP；
 * - `new_user_offer` 7 天窗、发现面归属排除与本页上架集合收窄（70 §3.10）对两个通道同等生效。
 */
const BLIND_SPOT_NEW_USER_WINDOW =
  'scenario=new_user_offer 的 7 天窗：注册超过 7 天的用户该场景直接返空态（与通道无关），本页不按用户判定。';
const BLIND_SPOT_DISCOVERY_OWNERSHIP =
  '④ 的残余漏报 —— 发现面归属：非发币 CONSUMABLE 效果各占一个专属发现面（买断行只在 scenario=novel_purchase、畅读卡行只在 reading_pass），在别的支付页一律被排除；未登记的效果 fail-closed、任何发现面都不进（C 端 consumable-effect-discovery-scenario.ts，契约 60 §0 / 70 §2.5.1）。**作用面只到 GET /payment/products 与 offers**（60 §1「收窄在通道判据之前」末句自限），运营弹窗页按 60 §2.1 的独立弹窗商品范围求值，见下一条。发币类（COIN_GRANT）与订阅不受此限。故本通道的可售行若恰好全是某个专属效果的行，admin 按 App 全量判「有可售行」而 C 端在别的页判空 —— ④ 只有「一条可售行都没有 → 不下发」这条否定式可靠，反向不成立。';
const BLIND_SPOT_PAGE_PRODUCT_SET =
  '④ 的场景级收窄：C 端判「存在可售产品」时只看各支付页自己的商品范围（该支付页的上架集合 ∩ 发现面 scenario 归属 ∩ is_serveable，70 §3.10），而这里按 App 全量判。若某支付页的上架集合里没有本通道的可售行，C 端在该页不下发本通道，本页核不到 —— 到「支付页面 → 上架商品」逐页查看。买断页（novel_purchase）**只豁免「上架集合」那一段**：它的本页商品范围仍是「发现面 scenario 归属 ∩ is_serveable」（70 §3.10 适用性判据），故本通道的可售行若全不属该发现面，C 端在买断页判空而本页判有，这半边差异照旧存在。运营弹窗页（GET /payment/popups）不读支付页面上架集合，按 60 §2.1 判可售：只看当前生效层（App 层有场景行则只取 App 层，否则取全局层）中已启用且 access_level_id 非空的弹窗场景所引用的商品，且一次性商品仅 COIN_GRANT，畅读卡、买断与其他非发币效果均不参与通道候选或 scenes 下发；订阅沿既有口径。因此本通道若只有非发币一次性商品，或可售行都不是任何启用场景的 access level，弹窗侧判空，本页按 App 全量仍可能判有。';

/** 框架通道的残余判据（各家同一套 C 端判定，只换供应商名）。 */
function frameworkChannelBlindSpots(provider: string): readonly string[] {
  return [
    '回跳 host 是否仍在 C 端当前环境为该 App 登记的 host 内、部署环境是否放行本通道且 mode 与配置一致：由 C 端判定，admin 读不到。',
    `${provider} 侧商户合约与支付方式开通状态：在 ${provider} 商户后台，本页核不到。`,
    `SRV-PAY-003 用户级收窄（第三方支付解锁）：用户 third_party_payment_unlocked_at 为 NULL 且候选里有 IAP 时，C 端把候选收窄成 {IAP}，${provider} 不下发 —— 逐用户不同，本页核不到。`,
    `SRV-PAY-033 审核账号收窄：带审核标签的账号候选被收窄成 {IAP}，${provider} 不下发。`,
    BLIND_SPOT_NEW_USER_WINDOW,
    BLIND_SPOT_DISCOVERY_OWNERSHIP,
    BLIND_SPOT_PAGE_PRODUCT_SET,
  ];
}

export const READINESS_BLIND_SPOTS: Record<SettlementChannel, readonly string[]> = {
  STRIPE: [
    '回跳 URL 的 host 是否仍属于 C 端当前环境为该 App 登记的回跳 host（PAYMENT_RETURN_HOSTS）：admin 写入时按本侧同值登记校验，C 端在下发与发起时按它自己那份复核，那份不在本进程内。该闸只对 STRIPE 生效，以 C 端 serving 为准。',
    'SRV-PAY-003 用户级收窄：用户 third_party_payment_unlocked_at 为 NULL 且候选里有 IAP 时，C 端把候选收窄成 {IAP}，STRIPE 不下发 —— 这是绝大多数用户的常态，且逐用户不同，本页核不到。',
    'SRV-PAY-033 审核账号收窄：带审核标签的账号候选被收窄成 {IAP}，STRIPE 的 payment_methods 行与对应 entitlements 一并不返。',
    BLIND_SPOT_NEW_USER_WINDOW,
    BLIND_SPOT_DISCOVERY_OWNERSHIP,
    BLIND_SPOT_PAGE_PRODUCT_SET,
  ],
  IAP: [BLIND_SPOT_NEW_USER_WINDOW, BLIND_SPOT_DISCOVERY_OWNERSHIP, BLIND_SPOT_PAGE_PRODUCT_SET],
  // 框架通道的配置就绪（已启用 / connected）、原生回跳、验签材料由本面板核为阻断（⑤⑥⑦），回调停用与逐商品选价出中性提示（⑦⑧），
  // 余下是后台读不到的部分：C 端环境侧判定、供应商商户侧状态与两条用户级收窄（同 STRIPE，收窄到 {IAP}）。
  PAYERMAX: frameworkChannelBlindSpots('PayerMax'),
  AIRWALLEX: frameworkChannelBlindSpots('Airwallex'),
};

/**
 * 单个数据源的就绪态；`ready` 之外一律 fail-closed。
 *
 * `not_requested` 与 `loading` 分开：查询被 `enabled=false` 禁用 / 断网被 paused 时既不在 loading、
 * 也没有 error，写成「加载失败」会把运营的排障方向带偏。
 */
export type ReadinessSourceState = 'loading' | 'not_requested' | 'error' | 'forbidden' | 'ready';

/**
 * hook 的 flag 组合 → 数据源就绪态。**只有 `isSuccess` 才算 ready。**
 *
 * 次序即判据优先级：拿到数据 > 无权限 > 失败 > 在飞 > 压根没发起。
 * `{ isLoading: false, isError: false, isSuccess: false }`（TanStack `fetchStatus='paused'` 的断网
 * 形态，或 `enabled=false`）落在最后一档 `not_requested` → `undeterminable`，**不会被判成 ready**。
 */
export function readinessSourceState(hook: {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isForbidden?: boolean;
}): ReadinessSourceState {
  if (hook.isSuccess) return 'ready';
  if (hook.isForbidden === true) return 'forbidden';
  if (hook.isError) return 'error';
  if (hook.isLoading) return 'loading';
  return 'not_requested';
}

export interface ReadinessSource<T> {
  state: ReadinessSourceState;
  data: T;
}

export type ReadinessCheckId =
  | 'app_active'
  | 'policy'
  | 'placement'
  | 'checkout_config'
  | 'provider_config'
  | 'native_return'
  | 'webhook_endpoint'
  | 'serveable_product'
  | 'channel_price';

/**
 * `blocked` = 查到了确定阻断下发的事实；
 * `undeterminable` = 数据没到，**不等于**无阻断；
 * `not_applicable` = 本 App 压根没启用该通道 —— 中性，不计阻断也不铺开其余判据；
 * `no_blocker_found` = 该项数据齐全且未查到阻断项 —— 仍**不等于**会下发。
 */
export type ReadinessCheckVerdict =
  | 'blocked'
  | 'undeterminable'
  | 'not_applicable'
  | 'no_blocker_found';

/** 该条目的处置入口；`null` = 无入口。Panel 按 target 决定渲染跳转按钮还是「需谁去处理」文案。 */
export type ReadinessActionTarget =
  | 'app_danger_tab'
  | 'policy_panel'
  | 'paywall_pages'
  | 'paywall_products'
  /** 支付管理 › 支付页面 › 运营弹窗页（operational_popup 落点的写入口，A8 §7.5）*/
  | 'paywall_popup_page'
  | 'stripe_integration'
  /** 设置 › 集成 › 该框架通道的配置与回调登记页（root / admin 写）*/
  | 'provider_config';

export interface ReadinessItem {
  /** 稳定 key（React list key + 测试定位）*/
  key: string;
  /** 该条目所属维度值：Region code / scenario / null（App 级）*/
  scopeLabel: string | null;
  text: string;
  /** 去哪儿处置；null = 无对应入口 */
  action: ReadinessActionTarget | null;
}

export interface ReadinessCheck {
  id: ReadinessCheckId;
  title: string;
  /** 该项的展开维度，写给运营看（避免把 per-region 的结论按 scenario 读）*/
  dimension: string;
  verdict: ReadinessCheckVerdict;
  /** 确定阻断下发的事实，计入 `blockerCount` */
  blockers: ReadinessItem[];
  /** 中性项（运营的刻意配置 / 未适用 / C 端不拦但会失败）—— **不计入 `blockerCount`** */
  notices: ReadinessItem[];
  /** `undeterminable` 时写明是哪份数据没到；其余为 null */
  unavailableReason: string | null;
}

export interface PaymentChannelReadiness {
  /** 本条就绪度面向的**结算通道**（① 策略成员经 `channelOf` 投影后汇总到本层）*/
  paymentMethod: SettlementChannel;
  checks: ReadinessCheck[];
  blockerCount: number;
  undeterminableCount: number;
  noticeCount: number;
  /** 本 App 未启用该通道（策略组内无成员）→ 中性态，其余判据不铺开 */
  notApplicable: boolean;
  /**
   * 数据齐全、未适用态不成立、且全部未查到阻断项。
   * **文案只能是「未发现阻断项」，不得渲染为「会被下发」的绿灯** —— 见 `READINESS_BLIND_SPOTS`。
   */
  noBlockerFound: boolean;
  /** 本通道**本页核不到**的残余判据（逐条列全，不写「一项」）*/
  blindSpots: readonly string[];
}

export interface PaymentChannelReadinessInput {
  appId: string;
  /**
   * `apps.is_active`（`AppResponse.is_active` / `AppRow.status === 'active'`）。
   * C 端 `findAppById` 的 where 含 `eq(apps.isActive, true)`，取不到 App 即抛 `APP_NOT_FOUND`
   * —— **任何通道都不下发**，故这是全通道最高优先级前置。
   */
  appIsActive: boolean;
  /** 结算通道层取值；策略成员是支付方式层，比对前先 `channelOf` 投影 */
  paymentMethod: SettlementChannel;
  policies: ReadinessSource<PaymentMethodPolicyResponse[]>;
  /** A8 §7.8.8 可选支付方式：成员 → 结算通道的唯一来源；未就绪时 ① 与依赖 ① 的判据一并不可判 */
  paymentMethodOptions: ReadinessSource<PaymentMethodOptionsResponse | null>;
  paymentMethods: ReadinessSource<AppPaymentMethodResponse[]>;
  /** `useStripeCheckoutConfigs` 是**全量 App 列表**，按 `app_id` 过滤在本函数内做 */
  stripeCheckoutConfigs: ReadinessSource<StripeCheckoutConfigResponse[]>;
  productMetadata: ReadinessSource<ProductMetadataRow[]>;
  /**
   * 该框架通道的各 App 配置（A8 §10.1，全量 App 列表，按 `app_id` 过滤在本函数内做）；IAP / STRIPE 不读，
   * 传 `not_requested` 即可。
   */
  paymentProviderConfigs: ReadinessSource<PaymentProviderConfigResponse[]>;
  /** 原生 App（非 WEB）才要求 `native_return_base_url`（字典 65 §1；WEB 恒 null） */
  isNativeApp: boolean;
}

function unavailableReason(source: { state: ReadinessSourceState }, what: string): string | null {
  switch (source.state) {
    case 'loading':
      return `${what}加载中`;
    case 'not_requested':
      return `${what}数据未就绪（查询未发起）`;
    case 'error':
      return `${what}加载失败`;
    case 'forbidden':
      return `${what}需 root/admin 权限查看（当前账号无权读取，非接口故障）`;
    default:
      return null;
  }
}

/**
 * ① 成员归属的两份输入（策略组 + 可选列表）任一未就绪时的原因；都就绪返 null。
 * 可选列表缺席时不能退回任何本地映射去投影成员 —— 那正是契约禁止的另一份 `channel_of`。
 */
function membershipUnavailableReason(input: PaymentChannelReadinessInput): string | null {
  return (
    unavailableReason(input.policies, '通道策略组') ??
    unavailableReason(input.paymentMethodOptions, '可选支付方式')
  );
}

/** 成员 → 结算通道（取 §7.8.8 `channel`）；调用方须先确认可选列表已就绪 */
function memberChannelOf(input: PaymentChannelReadinessInput) {
  return createPaymentMethodOptionLookup(input.paymentMethodOptions.data).channelOf;
}

function undeterminable(
  id: ReadinessCheckId,
  title: string,
  dimension: string,
  reason: string,
): ReadinessCheck {
  return {
    id,
    title,
    dimension,
    verdict: 'undeterminable',
    blockers: [],
    notices: [],
    unavailableReason: reason,
  };
}

function resolved(
  id: ReadinessCheckId,
  title: string,
  dimension: string,
  blockers: ReadinessItem[],
  notices: ReadinessItem[] = [],
): ReadinessCheck {
  return {
    id,
    title,
    dimension,
    verdict: blockers.length > 0 ? 'blocked' : 'no_blocker_found',
    blockers,
    notices,
    unavailableReason: null,
  };
}

function notApplicableCheck(
  id: ReadinessCheckId,
  title: string,
  dimension: string,
  notices: ReadinessItem[],
): ReadinessCheck {
  return {
    id,
    title,
    dimension,
    verdict: 'not_applicable',
    blockers: [],
    notices,
    unavailableReason: null,
  };
}

/**
 * ⓪ App 启停 —— per **App**，**全通道前置**。
 *
 * C 端 `src/db/repositories/apps.ts` `findAppById` 的 where 含 `eq(apps.isActive, true)`；route 取不到
 * App 即抛 `APP_NOT_FOUND`，`/payment` 全系列直接失败 —— 任何通道都不下发。
 *
 * ⚠️ 这与 `app-payment-methods-adapter.ts` 移除的 `app_disabled` 档**不是一回事**：那一档读的是已随
 * ADM-APP-S009 退役、C 端 serving 不再读取的**通道级开关**（`is_iap_enabled` / `is_stripe_enabled`），
 * 会与策略组给出相反结论；`apps.is_active` 是仍在 serving 路径上的 App 级闸，admin 侧完全核得到。
 */
function checkAppActive(input: PaymentChannelReadinessInput): ReadinessCheck {
  const title = '⓪ App 启停';
  const dimension = '按 App（全通道最高优先级前置）';
  if (input.appIsActive) return resolved('app_active', title, dimension, []);
  return {
    id: 'app_active',
    title,
    dimension,
    verdict: 'blocked',
    blockers: [
      {
        key: 'app:inactive',
        scopeLabel: null,
        text: 'App 已停用（is_active=false）—— C 端 findAppById 的 where 含 is_active=true，取不到 App 即抛 APP_NOT_FOUND，任何通道都不下发。在此前提下其余判据均无意义，故不再展开。',
        action: 'app_danger_tab',
      },
    ],
    notices: [],
    unavailableReason: null,
  };
}

/**
 * ① 策略组成员（A8 §7.8.0 / `03-数据字典/70-产品配置.md` §3.7）—— 按 **Region** 展开。
 *
 * 读取算法（C 端 `app-payment-method-policies.ts` 文件头）：`effective_store_region` 精确匹配的
 * 覆盖组优先，**组存在（含零成员）即采用、不回退全局**；组不存在才回退全局组。全局组本身不存在时
 * `policyMembers` 为空数组（`payment-channel-candidates.ts` `PaymentChannelPolicyContext` 注释），
 * 即所有未被覆盖组接住的地区候选集为空。
 *
 * **本 App 的策略组面板是通道启停的唯一入口**，故「组里没有本通道」= 运营关掉了它：
 * - 全局组与**所有** Region 覆盖组都不含本通道，**且该 App 别处仍有成员** → `not_applicable`
 *   （中性、零阻断、不铺开其余项）；
 * - 全局组不含但某覆盖组含 → 中性 notice（本通道只在那些地区下发），其余判据照常展开；
 * - 覆盖组刻意剔除本通道 → 中性 notice，措辞区分「刻意停用」（组内有其他成员）与「疑似漏配」（零成员组）。
 *
 * 三种情况是真阻断（配置缺失 / 全 App 中断，不是启停选择）：
 * 1. **一个策略组都没有** —— `policyMembers` 恒空；
 * 2. **有覆盖组却没有全局组** —— 未被覆盖组接住的地区候选集为空；
 * 3. **全 App 所有组加起来零成员** —— 任何地区任何用户都拿不到任何支付方式（充值中心空白）。
 *    这一条与「运营刻意关掉某个通道」的区别在于：那里至少还有别的通道在卖，这里整个 App 卖不出
 *    东西。「组存在（含零成员）即采用、不回退全局」意味着它不会被任何上层兜住。
 *
 *    **它现在也是新 App 的出厂状态**（srv 0199 / §3.7.4：建 App 只建全局组、初始零成员），不再
 *    只由运营主动清空造出。仍判阻断而不是中性：零成员不是「启停选择」——没有任何通道在卖，充值
 *    中心是空白页，而修复动作明确且就在本面板旁边（先配图标、再把支付方式加进全局组）。把它降成
 *    中性等于让一个卖不出东西的 App 显示「未发现阻断项」。
 */
function checkPolicy(input: PaymentChannelReadinessInput): ReadinessCheck {
  const title = '① 通道策略组';
  const dimension = '按 Region 展开';
  const reason = membershipUnavailableReason(input);
  if (reason !== null) return undeterminable('policy', title, dimension, reason);

  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  // 成员是**支付方式**层，本面板按结算通道分栏 —— 先按 §7.8.8 `channel` 投影再比，别字面比 `'STRIPE'`：
  // srv 0200 之后成员里根本不存在 `STRIPE`，字面比恒不命中，会把「已启用 Stripe」误报成「未启用」。
  const channelOf = memberChannelOf(input);
  const has = (policy: PaymentMethodPolicyResponse) =>
    policy.payment_methods.some((item) => channelOf(item.payment_method) === input.paymentMethod);
  const globalPolicy = input.policies.data.find((policy) => policy.region_code === null);
  const regionPolicies = input.policies.data.filter((policy) => policy.region_code !== null);
  const inGlobal = globalPolicy !== undefined && has(globalPolicy);
  const inAnyRegion = regionPolicies.some(has);
  // 全 App 范围内是否还有**任何**通道成员。判「未启用」的前提是「别的还在卖、只有我被关掉」——
  // 一个成员都不剩时那不叫启停选择，叫整个 App 的支付中断。
  const anyPolicyHasMembers = input.policies.data.some(
    (policy) => policy.payment_methods.length > 0,
  );

  // 未适用：策略组**结构完整**（至少一个组、全局组在位、全 App 尚有成员），但**没有任何一个**
  // 包含本通道 —— 运营的启停结果，不是阻断。
  //
  // 三个前置各挡一类被这条 early return 吞掉的真缺陷：
  // - `inAnyRegion`：某覆盖组含本通道时它在那些地区仍会下发，不能判「未启用」；
  // - `globalPolicy !== undefined`：结构缺陷先于启停判定，否则「有 Region 覆盖组、无全局组、
  //   且本通道不在任何覆盖组里」会被短路成中性，`policy:global-missing` 永不产出；
  // - `anyPolicyHasMembers`：**全 App 零成员**（典型形态是运营 PUT `payment_methods: []` 清空了
  //   唯一的全局组）时，两个通道都会被判「未启用」，面板对一个「任何地区任何用户都拿不到任何
  //   支付方式、充值中心空白」的 App 报 0 阻断 0 提示。这一支必须落到下面的 `policy:no-members`
  //   阻断。注意它与「全局组零成员但某覆盖组仍有成员」不同：后者本通道确实只是没被启用，
  //   而「全局组是空策略」这条事实会经 `policy:global-excluded` 在那个仍有成员的通道上说出来。
  if (
    input.policies.data.length > 0 &&
    globalPolicy !== undefined &&
    anyPolicyHasMembers &&
    !inGlobal &&
    !inAnyRegion
  ) {
    return notApplicableCheck('policy', title, dimension, [
      {
        key: 'policy:not-enabled',
        scopeLabel: null,
        text: `本 App 未启用 ${label} —— 上方「通道策略」面板的成员集合（全局组与各 Region 覆盖组）里都没有它。策略组是通道启停的唯一入口，这是运营的配置结果、不是配置缺陷，故不计入阻断项，其余判据也不再展开。`,
        action: 'policy_panel',
      },
    ]);
  }

  const blockers: ReadinessItem[] = [];
  const notices: ReadinessItem[] = [];

  if (input.policies.data.length === 0) {
    blockers.push({
      key: 'policy:none',
      scopeLabel: '全局',
      text: `该 App 一个通道策略组都没有 —— C 端 policyMembers 恒为空数组，候选集恒空，${label} 与其余通道都不会下发`,
      action: 'policy_panel',
    });
  } else if (globalPolicy === undefined) {
    blockers.push({
      key: 'policy:global-missing',
      scopeLabel: '全局',
      text: `该 App 有 Region 覆盖组但没有全局策略组 —— 未被覆盖组接住的地区候选通道集为空，${label} 在这些地区不会下发`,
      action: 'policy_panel',
    });
  } else if (!anyPolicyHasMembers) {
    // 组结构在位、但所有组（全局 + 各覆盖组）加起来一个成员都没有 —— 比「某个地区零成员」严重
    // 一档：后者只影响一个地区、且可能是刻意的地区策略，前者是整个 App 任何地区都卖不出东西。
    blockers.push({
      key: 'policy:no-members',
      scopeLabel: '全局',
      text: `该 App 的通道策略组全部是空策略（含全局组，共 ${input.policies.data.length} 个组、零成员）—— 所有地区的候选通道集恒空，${label} 与其余通道在任何地区、对任何用户都不会下发，充值中心将是空白。这不是「关掉某个通道」的启停结果：一个通道都没留下。`,
      action: 'policy_panel',
    });
  } else if (!inGlobal) {
    notices.push({
      key: 'policy:global-excluded',
      scopeLabel: '全局',
      text:
        globalPolicy.payment_methods.length === 0
          ? `全局策略组是空策略（零成员）—— 未被 Region 覆盖组接住的地区不返回任何通道；${label} 只在下方显式包含它的覆盖组地区下发`
          : `全局策略组内没有 ${label} —— 运营的刻意配置：${label} 只在下方显式包含它的 Region 覆盖组地区下发`,
      action: 'policy_panel',
    });
  }

  for (const policy of regionPolicies) {
    if (has(policy)) continue;
    notices.push({
      key: `policy:region:${policy.region_code}`,
      scopeLabel: policy.region_code,
      text:
        policy.payment_methods.length === 0
          ? `${policy.region_code} 覆盖组是空策略（零成员）—— 该地区不返回任何通道、且不回退全局。若非刻意，疑似漏配。`
          : `${policy.region_code} 覆盖组内没有 ${label}（组内已有 ${policy.payment_methods.length} 个其他支付方式）—— 该地区刻意停用本通道；覆盖组存在即采用、不回退全局。`,
      action: 'policy_panel',
    });
  }

  return resolved('policy', title, dimension, blockers, notices);
}

/**
 * 本通道是否是**任一**策略组（全局组或任一 Region 覆盖组）的成员。
 *
 * `null` = policies 未就绪，判不出来（fail-closed：调用方不得据此下确定性结论）。
 *
 * ② 的聚合判据要用它：`app_payment_methods` 行只在配页时插入，「某 scenario 没有本通道行」是常态；
 * 但「本通道在策略组成员内、却在**全部** scenario 下都没有行」是 admin 侧可独立证伪的确定性
 * 否定式 —— C 端 `findConfiguredPaymentMethods` 按 `(app_id, scenario)` 取行，取不到就没有任何
 * discovery 端点会返回它。
 *
 * **成员 = 运营意图**（srv 0199 / `03-数据字典/70-产品配置.md` §3.7.4）：建 App trigger 现在只建
 * 全局组、**初始零成员**，不再按 `apps.is_iap_enabled` 自动插 IAP 成员。故「组内有本通道」一定是
 * 运营经 A8 §7.8.2 主动加进去的（且加之前必须先配好图标）。0199 之前建的 App 仍可能带着 trigger
 * 自动插入的 IAP 成员，0200 的原位展开会把 `STRIPE` 成员展开成三个 `STRIPE_*` —— 存量成员的来历
 * 不影响本函数判据，它只回答「现在是不是成员」。
 */
function channelEnabledInAnyPolicy(input: PaymentChannelReadinessInput): boolean | null {
  if (membershipUnavailableReason(input) !== null) return null;
  const channelOf = memberChannelOf(input);
  return input.policies.data.some((policy) =>
    // 同 `checkPolicy`：成员是支付方式层，先投影到结算通道再比。
    policy.payment_methods.some((item) => channelOf(item.payment_method) === input.paymentMethod),
  );
}

/** scenario → 展示名（缺 label 时退回原值，不编造）*/
function scenarioLabel(scenario: string): string {
  return SCENARIO_LABELS[scenario as PaymentScenario] ?? scenario;
}

/**
 * ② placement 配置（A8 §7.7）—— 按 **scenario** 展开。
 *
 * C 端候选过滤第一条就是 `adaptyPlacementId === null → false`，且查询按 `(app_id, scenario)` 取行。
 *
 * **只有「行已建但 `adapty_placement_id` 为空」算阻断**：运营建了页却没配 placement，是真缺陷。
 * 「本通道在该 scenario 没有行」**不算阻断** —— `app_payment_methods` 行只在运营配页时插入
 * （`payment-pages-admin-service.ts` / copy App），建 App 不按
 * scenario 全集预置，故「没有行」是绝大多数 App 的常态、等同于「这个场景没打算卖」。两种「没有行」
 * 各合并成一条中性 notice：完全没建页 vs 建了页但没加本通道。
 *
 * **例外是全集为空**：中性只对**单个** scenario 成立。若本通道在策略组成员内，却在**全部**
 * `PAYMENT_SCENARIOS` 下都没有一行 —— C 端 `findConfiguredPaymentMethods` 在任何场景都取不到行，
 * 三个 discovery 端点无一会返它，这是 admin 侧可独立证伪的确定性否定式，**升为阻断**。
 *
 * **曾有的第三条前提「该 App 至少已有一行 `app_payment_methods`」已随契约退役**：它当初是为了
 * 挡住「建 App trigger 按 `is_iap_enabled` 自动插 IAP 成员」造成的 day-one 假红 —— 刚建、还没配过
 * 支付页的 App 会常驻一条只能靠删成员消音的阻断。srv 0199 把建 App 改成**全局组初始零成员**
 * （§3.7.4）后该前提失去依据：成员只可能由运营经 A8 §7.8.2 主动加入（且加之前必须先配图标），
 * 「已启用本通道、却一个场景都没配 placement」从第一天起就是可据以行动的确定性缺陷。
 *
 * **0199 之前建的存量 App 也不会因此常驻红 —— 这条有实测**：2026-09-20 只读核过 dev 与 prod
 * 两库，各 2 个非系统 App，每个都是「4 个成员 + 10~13 行 `app_payment_methods`」，
 * 「有成员却一行渠道行都没有」的 App 数为 **0**。那条前提当初要挡的正是这一类，现实里不存在。
 * （将来真出现时，正确修法是给它配页或把成员移出，而不是把前提加回来 —— 那会连真缺陷一起挡掉。）
 *
 * 策略组里没有本通道（或 policies 未就绪）时同样不升：前者是运营的启停结果，后者判不出来。
 */
function checkPlacement(input: PaymentChannelReadinessInput): ReadinessCheck {
  const title = '② placement 配置';
  const dimension = '按支付场景（scenario）展开';
  const reason = unavailableReason(input.paymentMethods, '支付渠道行');
  if (reason !== null) return undeterminable('placement', title, dimension, reason);

  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const blockers: ReadinessItem[] = [];
  const notices: ReadinessItem[] = [];
  const scenariosWithoutAnyRow: string[] = [];
  const scenariosWithoutChannelRow: string[] = [];
  let scenariosWithChannelRow = 0;

  for (const scenario of PAYMENT_SCENARIOS) {
    const scenarioRows = input.paymentMethods.data.filter((row) => row.scenario === scenario);
    const channelRow = scenarioRows.find((row) => row.payment_method === input.paymentMethod);
    if (channelRow === undefined) {
      if (scenarioRows.length === 0) scenariosWithoutAnyRow.push(scenario);
      else scenariosWithoutChannelRow.push(scenario);
      continue;
    }
    scenariosWithChannelRow += 1;
    if (channelRow.adapty_placement_id === null) {
      blockers.push({
        key: `placement:null:${scenario}`,
        scopeLabel: scenarioLabel(scenario),
        text: `${scenarioLabel(scenario)} 的 ${label} 行已建但未填 adapty_placement_id —— 该场景确定不下发本通道`,
        // operational_popup 直达该 App 的运营弹窗支付页（A8 §7.5 写入口；运营弹窗页的落点区只读）。
        action: scenario === 'operational_popup' ? 'paywall_popup_page' : 'paywall_pages',
      });
    }
  }

  // 全集为空 + 本通道在策略组成员内 → 阻断（见本函数注释「例外是全集为空」）。
  //
  // **不独占返回**：下面两条 per-scenario notice 语义互斥、不是同一批场景 —— `placement:no-rows`
  // 来自「该 scenario 一行都没有」（尚未建支付页），`placement:channel-missing` 来自「页已建、
  // 只是没加本通道」。只有「一张支付页都没建」时二者才退化成同一批，而那一档因上面的前提根本
  // 不会升到这条阻断。已经为别的通道建了页时二者非空且不相交，一并丢弃是真实信息损失：运营只能
  // 逐个打开 6 个场景页去看哪些已存在。噪声由 chip 侧「阻断非零时不叠加提示数」的规则兜住。
  if (scenariosWithChannelRow === 0 && channelEnabledInAnyPolicy(input) === true) {
    blockers.push({
      key: 'placement:none-anywhere',
      scopeLabel: null,
      // 陈述客观状态即可；成员归属由 `channelEnabledInAnyPolicy` 按 §7.8.8 `channel` 投影判定。
      text: `${label} 在通道策略组成员内（该通道下至少有一个支付方式是成员），但全部 ${PAYMENT_SCENARIOS.length} 个支付场景都没有它的渠道行 —— C 端按 (app_id, scenario) 取渠道行，任何场景都取不到即三个 discovery 端点都不会返回本通道。请在支付页面配置里至少给一个场景加上本通道，或在通道策略组里移除它。`,
      action: 'paywall_pages',
    });
  }

  if (scenariosWithoutAnyRow.length > 0) {
    notices.push({
      key: 'placement:no-rows',
      scopeLabel: null,
      text: `以下场景尚未建支付页（无任何渠道行，${label} 与其余通道都不会在该场景下发）：${scenariosWithoutAnyRow
        .map(scenarioLabel)
        .join('、')} —— 渠道行只在运营配页时插入，未建页是常态，不计入阻断项`,
      action: 'paywall_pages',
    });
  }

  if (scenariosWithoutChannelRow.length > 0) {
    notices.push({
      key: 'placement:channel-missing',
      scopeLabel: null,
      text: `以下场景已建支付页但未加入 ${label}：${scenariosWithoutChannelRow
        .map(scenarioLabel)
        .join('、')} —— 该场景不下发本通道；未加入不等于配置缺陷，不计入阻断项`,
      action: 'paywall_pages',
    });
  }

  return resolved('placement', title, dimension, blockers, notices);
}

/**
 * 回跳 URL 的 pathname 是否落在该字段的**允许集合**内。
 *
 * 判据与 C 端下发闸 `isStripeCheckoutReturnUrlPathAllowed`
 * （`novel-app-server` `src/lib/stripe/checkout-return-urls.ts`）**同口径**：`includes(pathname)`
 * 比对整个集合、只比 pathname，query / fragment 完全不参与。
 *
 * **必须是 `includes` 而不是 `=== 首位`**：C 端那份是数组，首位为终态 canonical、其后为迁移窗口内
 * 待下线的旧 path，窗口内仍配旧 path 的 App **照常下发**。这里若只比首位，改名窗口里 admin 会红色
 * 误报「STRIPE 不会下发」，运营据此去改一个正在正常收款的配置。本函数判的是「C 端会不会挡」，
 * 与 server 侧的 `isAllowedStripeRedirectPath`（既存行的读与判定，同样认全集）同档；只有**新填**
 * （PUT 入参）那一档才认首位，见 shared 常量注释。
 *
 * 解析失败 → fail-closed 判否（C 端 `new URL` 同样过不了）。
 */
function hasAllowedRedirectPath(
  url: string | null,
  field: keyof typeof STRIPE_REDIRECT_PATHS,
): boolean {
  try {
    if (url === null) return false;
    const allowedPaths: readonly string[] = STRIPE_REDIRECT_PATHS[field];
    return allowedPaths.includes(new URL(url).pathname);
  } catch {
    return false;
  }
}

/**
 * URL 是否带 query / fragment —— `redirect_path_conforms` 比本模块的 path 档**多要求**的一条
 * （server `baseUrlPathname`：`search === '' && hash === ''`）。server 另要求 path 段为字面写法（无反斜杠 / `%2e` / 点段），
 * 存量行都经写侧逐字比对写入、不会命中，这里不另设原因。
 *
 * 解析失败判否：那种 URL 在 path 档就已判否、走阻断分支，到不了本函数的调用点。
 */
function hasQueryOrFragment(url: string | null): boolean {
  try {
    if (url === null) return false;
    new URL(url);
    // 与 C 端 assertReturnBaseUrl 同口径：原串带 ?/# 即算，含 search 为空的 `path?`。
    return url.includes('?') || url.includes('#');
  } catch {
    return false;
  }
}

/**
 * ③ Stripe direct Checkout 配置（A8 §9.1）—— per **App**，**STRIPE 专有**（IAP 无此前置，
 * 见 C 端 `payment-channel-candidates.ts`：`channelOf(member.paymentMethod) === 'STRIPE'` 时才判
 * `hasActiveStripeCheckoutConfig`）。
 *
 * `redirect_path_conforms` **比 C 端严**：server 的 `isAllowedStripeRedirectPath` 在「pathname 落在
 * 允许集合内」之上还要求 `search === '' && hash === ''` 且 path 段为字面写法（后者存量行不会命中，见
 * `hasQueryOrFragment` 注释）；C 端下发闸
 * `isStripeCheckoutReturnUrlPathAllowed` 只比 `allowedPaths.includes(parsed.pathname)`。
 * 两者对「带 `?query` 的存量活配置」结论相反 —— 直接照 `redirect_path_conforms=false` 报「不会下发」
 * 会误报：C 端 path 闸不拦，用户点进去才在 Session 创建时 fail-closed。**「path 闸会挡」与
 * 「path 闸不拦、但 Session 必失败」的处置完全不同**；本页核不到回跳 host，不能据此承诺会被下发。
 *
 * **分档不看「带不带 query」，看 pathname 本身**：本函数手上就有 `success_url` / `cancel_url` 原串，
 * 直接照 `STRIPE_REDIRECT_PATHS`（与 C 端同形的允许集合）比 pathname 即可，不必拿
 * 「带 query」当「pathname 对不对」的代理 —— 那个代理在「pathname 错**且**带 query」时两个条件
 * 同时成立、会把 C 端确定拒收的存量配置（如 `…/payment/stripe/success?from=legacy`）降成中性档。
 *
 * 三档：pathname 不符 → 真阻断（不论带不带 query）；pathname 对 + 带 query / fragment → 中性 notice
 * （C 端不挡，但创建 Session 时必然失败）；pathname 对 + 无 query → 无事。
 *
 * **中性档的措辞必须由实际检查得出、不得由差集反推**：两侧判据（server 的 `conforms` 与本模块的
 * path 档）今天对存量行只差 query / fragment 这一条，但那是**当下的巧合**，`else` 里硬编码「带了 query /
 * fragment」等于把它当永久事实 —— round-3 让两侧取了不同的 path 档次，差集当场多出「pathname 在
 * 集合内但非首位」一类，运营会在一个连问号都没有的 URL 上读到「请去掉 query / fragment」。故这里
 * 显式 `hasQueryOrFragment` 判，判不出原因时给一条不撒谎的兜底 notice（同时充当两侧漂移的报警）。
 */
function checkCheckoutConfig(input: PaymentChannelReadinessInput): ReadinessCheck {
  const title = '③ Stripe Checkout 配置';
  const dimension = '按 App（STRIPE 专有前置）';
  const reason = unavailableReason(input.stripeCheckoutConfigs, 'Stripe Checkout 配置');
  if (reason !== null) return undeterminable('checkout_config', title, dimension, reason);

  // 该 hook 返全量 App 列表，取数后按 app_id 过滤
  const configs = input.stripeCheckoutConfigs.data.filter(
    (config) => config.app_id === input.appId,
  );
  const blockers: ReadinessItem[] = [];
  const notices: ReadinessItem[] = [];
  const policyUnknownReason =
    channelEnabledInAnyPolicy(input) === null
      ? (membershipUnavailableReason(input) ?? '通道策略组未就绪，无法判断本通道是否适用')
      : null;

  if (configs.length === 0) {
    // 空集只有在「已知该通道应当下发」时才是阻断。策略组未就绪时把它当阻断，
    // IAP-only App 会在 Checkout 全量缓存已热、policies 还在飞/失败时亮红，
    // 并引导运营去写一条 C 端根本不会列入候选的 STRIPE 配置。
    if (policyUnknownReason !== null) {
      return undeterminable('checkout_config', title, dimension, policyUnknownReason);
    }
    blockers.push({
      key: 'checkout:absent',
      scopeLabel: null,
      text: '该 App 未接入 direct Checkout（没有 Stripe Checkout 配置行）—— C 端不会把 STRIPE 列入候选',
      action: 'stripe_integration',
    });
    return resolved('checkout_config', title, dimension, blockers, notices);
  }

  const activeConfigs = configs.filter((config) => config.is_active);
  if (activeConfigs.length === 0) {
    if (policyUnknownReason !== null) {
      return undeterminable('checkout_config', title, dimension, policyUnknownReason);
    }
    blockers.push({
      key: 'checkout:inactive',
      scopeLabel: null,
      text: '该 App 的 Stripe Checkout 配置存在但未启用（is_active=false）—— C 端不会把 STRIPE 列入候选',
      action: 'stripe_integration',
    });
    return resolved('checkout_config', title, dimension, blockers, notices);
  }

  if (!activeConfigs.some((config) => config.redirect_path_conforms)) {
    // C 端的 path 闸逐 config 判：只要有一个活配置两条 URL 的 pathname 都命中，就不构成「不下发」。
    const pathOkConfigs = activeConfigs.filter(
      (config) =>
        hasAllowedRedirectPath(config.success_url, 'success_url') &&
        hasAllowedRedirectPath(config.cancel_url, 'cancel_url'),
    );
    if (pathOkConfigs.length === 0) {
      blockers.push({
        key: 'checkout:redirect-path',
        scopeLabel: null,
        // 期望值列**全集**（不只首位）：迁移窗口内旧 path 同样被 C 端放行，只报首位会让运营
        // 以为存量旧配置必须立刻改，而那恰恰不是本条阻断在说的事
        text: `启用中的 Stripe Checkout 配置回跳 URL 的 pathname 不在 C 端允许集内（期望 ${STRIPE_REDIRECT_PATHS.success_url.join(
          ' / ',
        )} 与 ${STRIPE_REDIRECT_PATHS.cancel_url.join(' / ')}）—— C 端的 path 闸会挡掉，STRIPE 不会下发`,
        action: 'stripe_integration',
      });
    } else if (
      pathOkConfigs.some(
        (config) => hasQueryOrFragment(config.success_url) || hasQueryOrFragment(config.cancel_url),
      )
    ) {
      notices.push({
        key: 'checkout:redirect-query',
        scopeLabel: null,
        text: '启用中的 Stripe Checkout 配置回跳 URL 的 pathname 命中 C 端 path 允许集，但带了 query / fragment，不符合「基础 URL」规范。C 端 path 闸不因此拦截；用户若走到创建 Session 会 fail-closed。本页核不到回跳 host，不能断定 STRIPE 会否被列入候选。请尽快去掉 query / fragment。',
        action: 'stripe_integration',
      });
    } else {
      // 兜底：pathname 命中允许集、也没带 query / fragment，服务端却仍判 conforms=false ——
      // 本模块复算不出原因，多半是两侧判据漂移（server 的 `isAllowedStripeRedirectPath` 加了新条件）。
      // 不编造原因、也不报「不会下发」：C 端的 path 闸只比 pathname，这类配置它确实照常放行。
      notices.push({
        key: 'checkout:redirect-unknown',
        scopeLabel: null,
        text: '启用中的 Stripe Checkout 配置回跳 URL 的 pathname 命中 C 端 path 允许集、也没带 query / fragment，但服务端仍判 redirect_path_conforms=false —— admin 侧复算不出原因（服务端的基础 URL 判据可能已收紧而本面板未跟进）。本页核不到回跳 host，不能断定 STRIPE 会否被列入候选；请到设置›集成页核对该配置。',
        action: 'stripe_integration',
      });
    }
  }

  return resolved('checkout_config', title, dimension, blockers, notices);
}

/** 连接测试失败原因（A8 §10.1.1 测试结果记录闭集）→ 运营可读说明 */
const PROVIDER_TEST_REASON_TEXT: Record<PaymentProviderTestResultReason, string> = {
  CREDENTIAL_INVALID: '拒绝了这组凭据',
  PERMISSION_DENIED: '商户账号无该接口权限',
};

/** 本 App 在该框架通道的配置行；调用方须先确认数据源 ready */
function providerConfigOf(
  input: PaymentChannelReadinessInput,
): PaymentProviderConfigResponse | null {
  return input.paymentProviderConfigs.data.find((config) => config.app_id === input.appId) ?? null;
}

/**
 * 框架通道配置类阻断（⑤⑥ 与 ⑦ 的「无验签材料」档）的收尾：查到阻断项但通道策略组未就绪时降为「无法判定」（与 ③ 同口径 ——
 * 阻断只有在「已知该通道应当下发」时才成立，否则会引导运营去改一个 C 端根本不列入候选的通道）。
 */
function resolvedUnlessPolicyUnknown(
  input: PaymentChannelReadinessInput,
  id: ReadinessCheckId,
  title: string,
  dimension: string,
  blockers: ReadinessItem[],
  notices: ReadinessItem[] = [],
): ReadinessCheck {
  if (blockers.length > 0 && channelEnabledInAnyPolicy(input) === null) {
    return undeterminable(
      id,
      title,
      dimension,
      membershipUnavailableReason(input) ?? '通道策略组未就绪，无法判断本通道是否适用',
    );
  }
  return resolved(id, title, dimension, blockers, notices);
}

/**
 * ⑤ 框架通道配置就绪（字典 65 §1「可售与发起」中后台读得到的部分）—— per **App**，框架通道专有。
 * 无本 App 配置行 / `is_active=false` / `connection_status≠connected`，任一命中即该通道不进候选。
 */
function checkProviderConfig(input: PaymentChannelReadinessInput): ReadinessCheck {
  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const title = '⑤ 配置就绪';
  const dimension = `${label} 配置 · 本 App 一行`;
  const reason = unavailableReason(input.paymentProviderConfigs, `${label} 配置`);
  if (reason !== null) return undeterminable('provider_config', title, dimension, reason);
  const config = providerConfigOf(input);
  const blockers: ReadinessItem[] = [];
  if (config === null) {
    blockers.push({
      key: 'provider:missing',
      scopeLabel: null,
      text: `本 App 尚未配置 ${label}（无配置行）—— C 端不会把 ${label} 列入候选`,
      action: 'provider_config',
    });
  } else {
    if (!config.is_active) {
      blockers.push({
        key: 'provider:inactive',
        scopeLabel: null,
        text: `${label} 配置未启用`,
        action: 'provider_config',
      });
    }
    if (config.connection_status === 'invalid') {
      const failed = config.last_test_result?.reason ?? null;
      blockers.push({
        key: 'provider:invalid',
        scopeLabel: null,
        text: failed
          ? `连接状态为已失效：${label} ${PROVIDER_TEST_REASON_TEXT[failed]}（${failed}）`
          : '连接状态为已失效',
        action: 'provider_config',
      });
    } else if (config.connection_status !== 'connected') {
      blockers.push({
        key: 'provider:unverified',
        scopeLabel: null,
        text: `连接未验证：${config.last_test_result === null ? '尚未测试' : '配置变更后需重新测试'}，启用前须测试通过`,
        action: 'provider_config',
      });
    }
  }
  return resolvedUnlessPolicyUnknown(input, 'provider_config', title, dimension, blockers);
}

/** ⑥ 原生回跳 —— 原生 App 的配置须填 `native_return_base_url`（字典 65 §1；WEB 恒 null，不适用）。 */
function checkNativeReturn(input: PaymentChannelReadinessInput): ReadinessCheck {
  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const title = '⑥ 原生回跳';
  const dimension = '原生 App 须填 native_return_base_url';
  const reason = unavailableReason(input.paymentProviderConfigs, `${label} 配置`);
  if (reason !== null) return undeterminable('native_return', title, dimension, reason);
  const config = providerConfigOf(input);
  if (config === null) {
    return resolved(
      'native_return',
      title,
      dimension,
      [],
      [
        {
          key: 'native-return:no-config',
          scopeLabel: null,
          text: '本 App 尚无配置行，回跳地址随配置一起填写（见 ⑤）',
          action: null,
        },
      ],
    );
  }
  const blockers: ReadinessItem[] =
    input.isNativeApp && config.native_return_base_url === null
      ? [
          {
            key: 'native-return:missing',
            scopeLabel: null,
            text: `原生 App 的 ${label} 配置未填 native_return_base_url —— 支付完成后无法回到 App，C 端不下发本通道`,
            action: 'provider_config',
          },
        ]
      : [];
  return resolvedUnlessPolicyUnknown(input, 'native_return', title, dimension, blockers);
}

/**
 * ⑦ 回调地址与验签材料 —— 商户账户组（merchant_account_id + mode）的未退役 endpoint（`webhook_endpoints[]`，A8 §10.1）。
 *
 * 两档（A8 §10.2）：
 * - 组内没有任何带当前有效验签材料的未退役 endpoint（列表为空 / 全部 `verifying_material_count=0`）→ **阻断**：
 *   同步响应验签的唯一来源是这组材料，没有就一律验签失败，新发起与存量订单的长期读取都无法完成
 *   （discovery 仍会下发，用户点了必失败）；
 * - 有材料、但没有「启用且自身有材料」的 endpoint → **中性提示**：停用不改变验签材料、新发起照常，只是回调
 *   全部 401（停用的 endpoint 拒收；启用但未写材料的 endpoint 同样 401）、退款与争议不再自动处理
 *   （字典 65 §1 的下发条件不含 endpoint）。
 */
function checkWebhookEndpoint(input: PaymentChannelReadinessInput): ReadinessCheck {
  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const title = '⑦ 回调地址';
  const dimension = '商户组内的 endpoint 与验签材料';
  const reason = unavailableReason(input.paymentProviderConfigs, `${label} 配置`);
  if (reason !== null) return undeterminable('webhook_endpoint', title, dimension, reason);
  const config = providerConfigOf(input);
  if (config === null) {
    return resolved(
      'webhook_endpoint',
      title,
      dimension,
      [],
      [
        {
          key: 'webhook:no-config',
          scopeLabel: null,
          text: '本 App 尚无配置行，回调地址按商户账户组登记（见 ⑤）',
          action: null,
        },
      ],
    );
  }
  const group = `${config.merchant_account_id} · ${config.mode}`;
  if (!config.webhook_endpoints.some((endpoint) => endpoint.verifying_material_count > 0)) {
    return resolvedUnlessPolicyUnknown(input, 'webhook_endpoint', title, dimension, [
      {
        key: 'webhook:no-material',
        scopeLabel: null,
        text: `商户组 ${group} 没有当前有效的验签材料（无未退役回调地址，或都未写入材料）：同步响应验签一律失败，新发起与存量订单的长期读取都无法完成，回调全部被拒（401）`,
        action: 'provider_config',
      },
    ]);
  }
  // 能收回调的 endpoint 须同时启用且自身有材料：新登记未写材料的 endpoint 回调同样 401（A8 §10.2）
  if (
    config.webhook_endpoints.some(
      (endpoint) => endpoint.is_active && endpoint.verifying_material_count > 0,
    )
  ) {
    return resolved('webhook_endpoint', title, dimension, []);
  }
  return resolved(
    'webhook_endpoint',
    title,
    dimension,
    [],
    [
      {
        key: 'webhook:none-active',
        scopeLabel: null,
        text: `商户组 ${group} 没有启用且已写入验签材料的回调地址：回调全部被拒（401），退款和争议通知不会自动处理；验签材料仍在，新发起不受影响`,
        action: 'provider_config',
      },
    ],
  );
}

/**
 * ⑧ 价格可选出 —— 在架框架通道商品行按能力声明 `currency_rule` 须选得出价格（`CHANNEL_PRICE_UNSELECTABLE`，
 * A8 §1 / 字典 65 §9）。按行报，不复刻选价判据（直接读服务端下发的原因）。
 *
 * **只出中性提示，不计阻断**：discovery 只剔除选不出价格的那几行，其余可售行照常下发；全部行都选不出价格时
 * `is_serveable` 全为 false，已由 ④ 报通道级阻断。
 */
function checkChannelPrice(input: PaymentChannelReadinessInput): ReadinessCheck {
  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const title = '⑧ 价格可选出';
  const dimension = `在架 ${label} 商品行有可用价格（按商品）`;
  const reason = unavailableReason(input.productMetadata, '产品配置');
  if (reason !== null) return undeterminable('channel_price', title, dimension, reason);
  const unpriced = input.productMetadata.data.filter(
    (row) =>
      row.paymentMethod === input.paymentMethod &&
      row.isActive &&
      row.validationIssues.includes('CHANNEL_PRICE_UNSELECTABLE'),
  );
  if (unpriced.length === 0) return resolved('channel_price', title, dimension, []);
  return resolved(
    'channel_price',
    title,
    dimension,
    [],
    [
      {
        key: 'price:unselectable',
        scopeLabel: null,
        text: `${unpriced.length} 个 ${label} 商品行选不出价格：${unpriced
          .map((row) => row.name || row.vendorProductId)
          .join('、')}（CHANNEL_PRICE_UNSELECTABLE）—— 这些商品在 ${label} 不下发`,
        action: 'paywall_products',
      },
    ],
  );
}

/**
 * ④ 可售产品（A8 §3 `is_serveable`）—— per `(app, payment_method)`，**不含 scenario 维度**。
 *
 * **直接消费服务端下发的 `is_serveable`，不在前端复刻可售判据**（admin 侧另有一份复刻，本刀不叠第二份）。
 *
 * 口径注记：C 端 `/payment/products` 把可售范围收窄到**本页商品范围**（该页上架集合 ∩ 发现面
 * scenario 归属 ∩ `is_serveable`，70 §3.10），即实际参与 serving 的集合是本集合的**子集**。
 * 「买断行被剔掉」**就是归属那一腿**、不是额外一刀，且同一腿同样剔掉畅读卡行
 *（srv `isEffectDiscoverableInScenario`：非发币效果只在其专属 scenario 放行）。故「一条可售行都没有 → 不下发」这条否定式在各口径下都成立
 * （更严的口径只会更空）；反向的「有可售行 → 会下发」本模块不断言，残余漏报见 `READINESS_BLIND_SPOTS`。
 */
function checkServeableProduct(input: PaymentChannelReadinessInput): ReadinessCheck {
  const title = '④ 可售产品';
  const dimension = '按 App × 通道（不分场景）';
  const reason = unavailableReason(input.productMetadata, '产品配置');
  if (reason !== null) return undeterminable('serveable_product', title, dimension, reason);

  const label = READINESS_CHANNEL_LABEL[input.paymentMethod];
  const channelRows = input.productMetadata.data.filter(
    (row) => row.paymentMethod === input.paymentMethod,
  );
  const serveableRows = channelRows.filter((row) => row.isServeable);
  const blockers: ReadinessItem[] = [];

  if (serveableRows.length === 0) {
    // 与 ③ 同：空集只有在已知本通道应当下发时才是阻断。策略组未就绪时升档，
    // IAP-only 会假报「无可售 STRIPE 产品」并引导去支付产品配置。
    if (channelEnabledInAnyPolicy(input) === null) {
      return undeterminable(
        'serveable_product',
        title,
        dimension,
        membershipUnavailableReason(input) ?? '通道策略组未就绪，无法判断本通道是否适用',
      );
    }
    blockers.push({
      key: 'product:none-serveable',
      scopeLabel: null,
      text:
        channelRows.length === 0
          ? `该 App 没有任何 ${label} 产品行 —— 候选集要求至少一个可售产品，本通道不会下发`
          : `该 App 的 ${channelRows.length} 条 ${label} 产品行当前全部不可售（is_serveable=false）—— 本通道不会下发`,
      action: 'paywall_products',
    });
  }

  return resolved('serveable_product', title, dimension, blockers);
}

/**
 * 计算某通道的负向清单。
 *
 * 两处**短路**（其后判据在该前提下无意义，铺开只会制造噪声）：
 * 1. ⓪ App 已停用 → 全通道最高优先级阻断，不再展开 ①②③④；
 * 2. ① 本 App 未启用该通道 → 中性未适用态，不再展开 ②③④。
 *
 * **`notApplicable` 与 ⓪ 正交，恒求值一次**：它只依赖 `policies` 一份数据源，与 App 启停无关，
 * 放在 `appActive.verdict !== 'blocked'` 分支内会让停用 App 的未启用通道退回初值 `false`，
 * 「本 App 未启用 STRIPE」这条中性事实一条都不说。`checks` 列表仍只在 ⓪ 未阻断时才铺开 ①②③④。
 *
 * ⚠️ 恒求值**不是**为了防「假阳性在停用态下复现」—— 停用态下 `checks` 恒为 `['app_active']`，
 * 那 1 项阻断就是 ⓪ 本身，②③④ 那种三条式假阳性结构上铺不出来。真正要防的是相反的一侧：
 * `notApplicable` 为真时汇总 chip 曾走中性早返、把 ⓪ 的阻断吞掉，让一个已停用 App 的未启用通道
 * chip 与健康 App 的逐字同色一致。呈现优先级由 `ReadinessSummaryChip` 负责，见该组件注释。
 *
 * IAP 不含 ③（Checkout 配置是 STRIPE 专有前置）；框架通道另核 ⑤–⑧（配置就绪 / 原生回跳 / 回调地址 / 价格可选出）；
 * **各通道都不画绿灯** —— 全不命中时一律是
 * 「未发现阻断项」，含义完全一致。
 */
export function buildPaymentChannelReadiness(
  input: PaymentChannelReadinessInput,
): PaymentChannelReadiness {
  const blindSpots = READINESS_BLIND_SPOTS[input.paymentMethod];

  const appActive = checkAppActive(input);
  // 与 ⓪ 正交：即便 App 已停用也先算一次，供汇总 chip 走「未启用」而不是红色「N 项阻断」
  const policy = checkPolicy(input);
  const notApplicable = policy.verdict === 'not_applicable';

  const checks: ReadinessCheck[] = [appActive];
  if (appActive.verdict !== 'blocked') {
    checks.push(policy);
    if (!notApplicable) {
      checks.push(checkPlacement(input));
      if (input.paymentMethod === 'STRIPE') checks.push(checkCheckoutConfig(input));
      checks.push(checkServeableProduct(input));
      // 框架通道专有：⑤ 配置就绪 · ⑥ 原生回跳 · ⑦ 回调地址 · ⑧ 价格可选出
      if (isFrameworkSettlementChannel(input.paymentMethod)) {
        checks.push(
          checkProviderConfig(input),
          checkNativeReturn(input),
          checkWebhookEndpoint(input),
          checkChannelPrice(input),
        );
      }
    }
  }

  const blockerCount = checks.reduce((sum, check) => sum + check.blockers.length, 0);
  const noticeCount = checks.reduce((sum, check) => sum + check.notices.length, 0);
  const undeterminableCount = checks.filter((check) => check.verdict === 'undeterminable').length;

  return {
    paymentMethod: input.paymentMethod,
    checks,
    blockerCount,
    undeterminableCount,
    noticeCount,
    notApplicable,
    // fail-closed：数据没到就不能说「没有阻断项」；未适用态自有措辞，不复用「未发现阻断项」
    noBlockerFound: !notApplicable && blockerCount === 0 && undeterminableCount === 0,
    blindSpots,
  };
}
